import React, { useCallback, useEffect, useState } from "react";
import { maintenanceApi, ProcManufacturer } from "../../../api/maintenance";
import { canEditMaintenance } from "../shared/MaintenanceGuard";
import { errMsg } from "./procUtils";
import { Plus, User, Mail, Phone, MapPin } from "lucide-react";

interface Props {
  refreshKey: number;
  onChanged: (msg?: string) => void;
  onError: (msg: string) => void;
}

const EMPTY: Partial<ProcManufacturer> = {
  name: "",
  contact_person: "",
  email: "",
  phone: "",
  address: "",
  gstin: "",
  notes: "",
};

/** Saved manufacturer profiles — picked at order time instead of retyping an email. */
const ManufacturersTab: React.FC<Props> = ({ refreshKey, onChanged, onError }) => {
  const [items, setItems] = useState<ProcManufacturer[]>([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState<Partial<ProcManufacturer> | null>(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");

  const canEdit = canEditMaintenance();

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await maintenanceApi.procGetManufacturers();
      setItems(res.data);
    } catch (e: any) {
      onError(errMsg(e, "Failed to load manufacturers"));
    } finally {
      setLoading(false);
    }
  }, [onError]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems, refreshKey]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editing?.name?.trim()) {
      setFormError("Name is required");
      return;
    }
    setSaving(true);
    setFormError("");
    try {
      const payload = { ...editing, name: editing.name.trim() };
      if (editing.id) {
        await maintenanceApi.procUpdateManufacturer(editing.id, payload);
      } else {
        await maintenanceApi.procCreateManufacturer(payload);
      }
      setEditing(null);
      fetchItems();
      onChanged(`Manufacturer ${editing.id ? "updated" : "added"}`);
    } catch (err: any) {
      setFormError(errMsg(err, "Failed to save manufacturer"));
    } finally {
      setSaving(false);
    }
  };

  const set = (k: keyof ProcManufacturer, v: string) =>
    setEditing((prev) => ({ ...(prev || {}), [k]: v }));

  return (
    <>
      <div className="maint-toolbar">
        <div>
          <div className="proc-mini">
            Saved contacts you raise orders against — pick one instead of retyping an email.
          </div>
        </div>
        {canEdit && (
          <button className="maint-btn-primary" onClick={() => setEditing({ ...EMPTY })}>
            <Plus size={16} /> Add manufacturer
          </button>
        )}
      </div>

      {loading ? (
        <p className="maint-loading">Loading…</p>
      ) : (
        <div className="proc-mfr-grid">
          {items.map((m) => (
            <div className="proc-mfr-card" key={m.id}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                <div className="mn">{m.name}</div>
                {canEdit && (
                  <button className="maint-btn-secondary maint-btn-small" onClick={() => setEditing(m)}>
                    Edit
                  </button>
                )}
              </div>
              <div className="mrow">
                <User size={14} /> {m.contact_person || "—"}
              </div>
              <div className="mrow">
                <Mail size={14} />
                {m.email ? (
                  <a href={`mailto:${m.email}`} style={{ color: "#5554d3" }}>
                    {m.email}
                  </a>
                ) : (
                  <span style={{ color: "#c62828" }}>no email — orders can't be emailed</span>
                )}
              </div>
              <div className="mrow">
                <Phone size={14} /> {m.phone || "—"}
              </div>
              <div className="mrow">
                <MapPin size={14} /> <span className="proc-mini">{m.address || "—"}</span>
              </div>
              <div
                style={{
                  borderTop: "1px solid #f0f0f0",
                  marginTop: 12,
                  paddingTop: 10,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <span className="proc-mini">GSTIN {m.gstin || "—"}</span>
                <span className={`proc-pill ${m.units_ordered ? "green" : "grey"}`}>
                  {m.units_ordered} unit{m.units_ordered === 1 ? "" : "s"} ordered
                </span>
              </div>
            </div>
          ))}
          {items.length === 0 && (
            <p className="maint-empty">No manufacturers yet — add one to raise your first order.</p>
          )}
        </div>
      )}

      {editing && (
        <div className="maint-form-overlay" onClick={() => setEditing(null)}>
          <div className="maint-form-card" onClick={(e) => e.stopPropagation()}>
            <div className="maint-form-header">
              <h3>{editing.id ? "Edit" : "Add"} manufacturer</h3>
              <button className="maint-btn-icon" onClick={() => setEditing(null)}>
                ✕
              </button>
            </div>
            <form onSubmit={handleSave}>
              <div className="maint-form-row">
                <label>
                  Name *
                  <input value={editing.name || ""} onChange={(e) => set("name", e.target.value)} />
                </label>
                <label>
                  Contact person
                  <input
                    value={editing.contact_person || ""}
                    onChange={(e) => set("contact_person", e.target.value)}
                  />
                </label>
              </div>
              <div className="maint-form-row">
                <label>
                  Email
                  <input
                    value={editing.email || ""}
                    onChange={(e) => set("email", e.target.value)}
                    placeholder="orders@…"
                  />
                </label>
                <label>
                  Phone
                  <input value={editing.phone || ""} onChange={(e) => set("phone", e.target.value)} />
                </label>
              </div>
              <label>
                Address
                <input value={editing.address || ""} onChange={(e) => set("address", e.target.value)} />
              </label>
              <div className="maint-form-row">
                <label>
                  GSTIN
                  <input value={editing.gstin || ""} onChange={(e) => set("gstin", e.target.value)} />
                </label>
                <label>
                  Notes
                  <input value={editing.notes || ""} onChange={(e) => set("notes", e.target.value)} />
                </label>
              </div>

              {formError && <p className="maint-form-error">{formError}</p>}

              <div className="maint-form-actions">
                <button type="button" className="maint-btn-secondary" onClick={() => setEditing(null)}>
                  Cancel
                </button>
                <button type="submit" className="maint-btn-primary" disabled={saving}>
                  {saving ? "Saving…" : "Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default ManufacturersTab;
